import { humanizeChannel } from '../lib/copy-widget.utils';
import type { CopyGenerationItem, CopyGenerationWidgetData } from '../model/copy-widget.types';
import { useTranslation } from 'react-i18next';

// Only the first variation previews in the fixed thumbnail box; rest clip.
const MAX_PREVIEW_ITEMS = 1;

interface Props {
  data: string | CopyGenerationWidgetData;
}

const parseData = (raw: string | CopyGenerationWidgetData): CopyGenerationWidgetData => {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw) as CopyGenerationWidgetData;
  } catch {
    return { items: [] };
  }
};

const captionText = (item: CopyGenerationItem): string =>
  item.text ?? item.body ?? item.subject ?? item.headline ?? '';

// Normalize to the full widget's `#tag` form — the data stores bare tags, and a
// lone `SipTheSummer` doesn't read as a hashtag. Tolerate tags that already
// carry a leading `#` so we never double it.
const hashtagsText = (item: CopyGenerationItem): string | null =>
  Array.isArray(item.hashtags) && item.hashtags.length > 0
    ? item.hashtags.map((tag) => `#${tag.replace(/^#+/, '')}`).join(' ')
    : null;

interface PreviewSection {
  header: string;
  caption: string;
  hashtags: string | null;
}

/**
 * Store-free, prop-only preview of a copy-generation widget. Echoes the full
 * widget's structure — a `{Channel} — Variation {n}` header, then the caption
 * with its own line breaks preserved (so a hooks/numbered list reads as a list,
 * not one run-on line) and the `#`-prefixed hashtags. Only the first variation
 * shows, so the caption gets the room to fill the box. Never touches the widget
 * store or export deps.
 */
export const CopyThumbnail = (props: Props) => {
  const { t } = useTranslation('contentTest');

  const data = parseData(props.data);
  const items = Array.isArray(data.items) ? data.items : [];

  // Mirror the full widget's per-channel variation numbering before clamping,
  // so "Variation 1" is the first item of its channel even when later ones clip.
  const perChannelCount: Partial<Record<string, number>> = {};
  const sections: PreviewSection[] = items.slice(0, MAX_PREVIEW_ITEMS).map((item) => {
    const next = (perChannelCount[item.channel] ?? 0) + 1;
    perChannelCount[item.channel] = next;
    return {
      header: t('copyWidget.sectionHeader', {
        channel: humanizeChannel(item.channel),
        variation: next,
      }),
      caption: captionText(item),
      hashtags: hashtagsText(item),
    };
  });

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <p className="truncate text-sm font-semibold text-[#1A1A1A]">
        {data.title ?? t('copyWidget.copyThumbnail.copy')}
      </p>
      <ol className="flex min-h-0 flex-col gap-2 overflow-hidden">
        {sections.map((section, index) => (
          <li key={index} className="flex flex-col gap-0.5">
            <span className="truncate text-xs font-semibold text-[#1A1A1A]">{section.header}</span>
            {section.caption && (
              <span className="line-clamp-6 whitespace-pre-line text-xs text-[#6C6962]">
                {section.caption}
              </span>
            )}
            {section.hashtags != null && (
              <span className="line-clamp-2 text-xs text-[#1A1A1A]">{section.hashtags}</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};
